import { gsap } from 'gsap/dist/gsap.min.js';
import ScrollTrigger from 'gsap/dist/ScrollTrigger.min.js';

const ERROR_MESSAGES = {
	ELEMENT_NOT_FOUND: 'Timeline: Required elements not found',
	INIT_FAILED: 'Timeline: Initialization failed',
	UPDATE_FAILED: 'Timeline: Failed to update squares',
	REFRESH_FAILED: 'Timeline: Failed to refresh timeline'
};

const CONFIG = {
	ANIMATION: {
		DURATION: 0.3,
		EASE: 'power1.inOut',
		OPACITY_DURATION: 0.3
	},
	COLORS: {
		ACTIVE: '#F3C97F',
		FILL_LIGHT: '#FFFFFF',
		FILL_DARK: '#151a30'
	},
	SCROLL_TRIGGER: {
		START: 'top 40%',
		END: 'bottom bottom',
		VISIBILITY_THRESHOLD: 0.1
	},
	SELECTORS: {
		FLOATING_BUTTON: '.floating-button',
		TIMELINE_WIDGET: '.widget-timeline',
		SQUARES: '[id^="scroll-path-square-"]'
	},
	TRIGGER_POINTS: [0.00159, 0.25135, 0.33375, 0.58, 0.66432, 0.90833, 1]
};

/**
 * Initialize timeline functionality
 * @returns {void}
 */
const initializeTimeline = () => {
	try {
		gsap.registerPlugin(ScrollTrigger);

		const floatingButton = document.querySelector(CONFIG.SELECTORS.FLOATING_BUTTON);
		const mainPath = document.getElementById('scroll-path');
		const squares = document.querySelectorAll(CONFIG.SELECTORS.SQUARES);

		if (!mainPath || !squares.length) {
			console.warn(ERROR_MESSAGES.ELEMENT_NOT_FOUND);
			return;
		}

		const pathLength = mainPath.getTotalLength();

		// Set up initial path state
		mainPath.style.strokeDasharray = pathLength;
		mainPath.style.strokeDashoffset = pathLength;

		/**
		 * Create timeline animations for squares
		 * @returns {Array<GSAPTimeline>}
		 */
		const createSquareTimelines = () => {
			return Array.from(squares).map((square) =>
				gsap.timeline({ paused: true }).to(square, {
					stroke: CONFIG.COLORS.ACTIVE,
					fill: CONFIG.COLORS.FILL_LIGHT,
					duration: CONFIG.ANIMATION.DURATION,
					ease: CONFIG.ANIMATION.EASE,
					onStart: () => {
						square.nextElementSibling.setAttribute('fill', CONFIG.COLORS.FILL_DARK);
					},
					onReverseComplete: () => {
						square.nextElementSibling.setAttribute('fill', CONFIG.COLORS.FILL_LIGHT);
					},
				})
			);
		};

		const squareTimelines = createSquareTimelines();

		/**
		 * Update squares based on scroll progress
		 * @param {number} progress - Current scroll progress (0-1)
		 * @returns {void}
		 */
		const updateSquares = (progress) => {
			try {
				requestAnimationFrame(() => {
					squares.forEach((_, index) => {
						if (progress >= CONFIG.TRIGGER_POINTS[index]) {
							squareTimelines[index].progress(1);
						} else {
							squareTimelines[index].progress(0);
						}
					});
				});
			} catch (error) {
				console.error(ERROR_MESSAGES.UPDATE_FAILED, error);
			}
		};

		/**
		 * Update floating button visibility
		 * @param {number} progress - Current scroll progress (0-1)
		 * @returns {void}
		 */
		const updateFloatingButton = (progress) => {
			if (!floatingButton) return;

			const isVisible = progress > CONFIG.SCROLL_TRIGGER.VISIBILITY_THRESHOLD && progress < 1;
			floatingButton.setAttribute('aria-hidden', (!isVisible).toString());
			gsap.to(floatingButton, { 
				autoAlpha: isVisible ? 1 : 0, 
				duration: CONFIG.ANIMATION.OPACITY_DURATION 
			});
		};

		// Create main path animation
		const mainAnimation = gsap.to(mainPath, {
			strokeDashoffset: 0,
			stroke: CONFIG.COLORS.ACTIVE,
			ease: 'none',
			scrollTrigger: {
				trigger: CONFIG.SELECTORS.TIMELINE_WIDGET,
				start: CONFIG.SCROLL_TRIGGER.START,
				end: CONFIG.SCROLL_TRIGGER.END,
				scrub: true,
				immediateRender: false,
				invalidateOnRefresh: true,
				onUpdate: (self) => {
					updateSquares(self.progress);
					updateFloatingButton(self.progress);
				},
				onRefresh: (self) => {
					updateSquares(self.progress);
					updateFloatingButton(self.progress);
				},
			},
		});

		/**
		 * Handle page refresh and resize
		 * @returns {void}
		 */
		const handleRefresh = () => {
			try {
				const scrollPosition = window.pageYOffset;
				window.scrollTo(0, scrollPosition);
				ScrollTrigger.refresh();
				const progress = mainAnimation.scrollTrigger.progress;
				updateSquares(progress);
			} catch (error) {
				console.error(ERROR_MESSAGES.REFRESH_FAILED, error);
			}
		};

		// Add event listeners
		window.addEventListener('load', handleRefresh);
		window.addEventListener('resize', handleRefresh);

		// Force initial refresh
		ScrollTrigger.refresh();
	} catch (error) {
		console.error(ERROR_MESSAGES.INIT_FAILED, error);
	}
};

export { initializeTimeline };
